import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRightIcon, CheckIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { pollWithWake } from '../../data/kioskRefresh';
import { SecurityMapOverlay } from '../kiosk/widgets/SecurityWidget';
import {
  fetchSecurityEvents,
  useSecurityState,
  type ContactDef,
  type SecEvent,
} from '../kiosk/widgets/houseState';
import { LIGHT_ENTITY_IDS } from '../kiosk/widgets/lights';
import { CardShell, StatusTile } from './HomeCard';
import type { TokenVar } from './wash';
import { BulbLineIcon, DoorLineIcon, WindowLineIcon } from './statusIcons';

// The home screen's lead card: is the house shut, and how many lights are on.
// Tapping it opens the kiosk's full house map.
//
// Polling matches the old dashboard banner it replaces: 15s and paused while
// the app is backgrounded, against the wall panel's 3s. pollWithWake re-ticks
// on visibilitychange, so returning to the app refreshes immediately.
const PHONE_POLL_MS = 15_000;
// The 24h door/window history behind the status line ("Front Door closed 2h
// ago"). It only feeds a caption, so it refreshes slowly — and immediately
// whenever something opens or closes.
const HISTORY_POLL_MS = 5 * 60 * 1000;

function isWindow(def: ContactDef): boolean {
  return /window/i.test(def.label);
}

// "12 min", "2h", "3d" — a duration short enough for one line on a phone.
function durationText(ms: number): string {
  const min = Math.max(1, Math.floor(ms / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function sinceText(ms: number): string {
  return ms < 60_000 ? 'just now' : `${durationText(ms)} ago`;
}

// Door/window events from the last 24h, newest first, refetched when the set
// of open contacts changes.
function useContactEvents(openKey: string): SecEvent[] | null {
  const [events, setEvents] = useState<SecEvent[] | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => {
      if (document.hidden) return;
      fetchSecurityEvents()
        .then(
          (ev) =>
            live && setEvents(ev.filter((e) => e.action === 'opened' || e.action === 'closed'))
        )
        .catch(() => live && setEvents((prev) => prev ?? []));
    };
    load();
    const stop = pollWithWake(load, HISTORY_POLL_MS);
    return () => {
      live = false;
      stop();
    };
  }, [openKey]);
  return events;
}

type Tone = 'secure' | 'alert' | 'idle';

const TONE: Record<Tone, { text: string; wash: TokenVar; badge: TokenVar }> = {
  secure: { text: 'text-green', wash: '--berg-green-soft', badge: '--berg-green' },
  alert: { text: 'text-red-light', wash: '--berg-red-soft', badge: '--berg-red-light' },
  idle: { text: 'text-fg-secondary', wash: '--berg-neutral-ghost', badge: '--berg-text-tertiary' },
};

export function SecurityCard() {
  const { states, error, nowTick, closedAt, openContacts, applyLights } = useSecurityState({
    pollMs: PHONE_POLL_MS,
    pauseWhenHidden: true,
  });
  const [open, setOpen] = useState(false);

  const loaded = Object.keys(states).length > 0;
  const openKey = openContacts.map((d) => d.entities[0]).join(',');
  const events = useContactEvents(openKey);

  const doorsOpen = openContacts.filter((d) => !isWindow(d)).length;
  const windowsOpen = openContacts.filter(isWindow).length;
  const lightsOn = LIGHT_ENTITY_IDS.filter((id) => states[id]?.state === 'on').length;

  // A poll error with nothing cached means we can't say the house is secure.
  const offline = !loaded && error !== null;
  const tone: Tone = !loaded ? 'idle' : openContacts.length > 0 ? 'alert' : 'secure';

  let title: string;
  let detail: string;
  if (offline) {
    title = 'Not connected';
    detail = "Can't reach the house right now";
  } else if (!loaded) {
    title = 'Checking…';
    detail = '';
  } else if (openContacts.length === 0) {
    title = 'All secure';
    const last = events?.[0];
    detail = last
      ? `${last.label} ${last.action} ${sinceText(nowTick - last.at)}`
      : events
        ? 'No doors or windows opened today'
        : '';
  } else if (openContacts.length === 1) {
    const def = openContacts[0];
    title = `${def.label} open`;
    const opened = events?.find((e) => e.key === def.entities[0] && e.action === 'opened');
    detail = opened ? `Open for ${durationText(nowTick - opened.at)}` : 'Open now';
  } else {
    title = `${openContacts.length} open`;
    detail = openContacts.map((d) => d.label).join(', ');
  }

  const t = TONE[tone];
  const hint = offline ? 'text-fg-secondary' : t.text;

  return (
    <>
      <CardShell
        wash={t.wash}
        onClick={() => setOpen(true)}
        ariaLabel={`Security: ${title}. Open the house map`}
        className="w-full flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 p-5 lg:p-7"
      >
        <span className="flex flex-col gap-4 lg:w-[300px] lg:shrink-0">
          <span className="flex items-center justify-between lg:justify-start gap-2">
            <span className="text-[17px] lg:text-[19px] font-semibold">Security</span>
            <ArrowUpRightIcon
              size={20}
              weight="bold"
              className="shrink-0 text-fg-secondary group-hover:text-fg transition-colors"
              aria-hidden
            />
          </span>
          {/* Two fixed lines, whatever the state, so the card never changes
              height as the house opens and closes. */}
          <span className="h-[72px] flex flex-col justify-center gap-1 min-w-0">
            <span
              className={clsx(
                'text-[30px] lg:text-4xl font-bold tracking-[-0.03em] truncate',
                offline ? 'text-fg-secondary' : t.text
              )}
            >
              {title}
            </span>
            <span className="text-sm text-fg-secondary truncate">{detail}</span>
          </span>
          <span className={clsx('hidden lg:block text-sm', hint)}>Click to open the house map</span>
        </span>

        <span className="grid grid-cols-3 gap-2 lg:gap-3 lg:flex-1">
          <StatusPart
            icon={<DoorLineIcon />}
            label={doorsOpen === 1 ? 'Door' : 'Doors'}
            state={doorsOpen > 0 ? 'open' : 'closed'}
            count={doorsOpen}
            known={loaded}
          />
          <StatusPart
            icon={<WindowLineIcon />}
            label={windowsOpen === 1 ? 'Window' : 'Windows'}
            state={windowsOpen > 0 ? 'open' : 'closed'}
            count={windowsOpen}
            known={loaded}
          />
          <LightsPart count={lightsOn} known={loaded} />
        </span>

        <span className={clsx('lg:hidden text-[13px] text-center', hint)}>
          Tap to open the house map
        </span>
      </CardShell>

      {/* Portalled to <body> so the fullscreen overlay doesn't inherit the
          page's flow spacing. */}
      {open &&
        createPortal(
          <SecurityMapOverlay
            onClose={() => setOpen(false)}
            sourceRect={null}
            header={<span className="block truncate text-sm font-semibold">Security</span>}
            states={states}
            nowTick={nowTick}
            closedAt={closedAt}
            onLights={applyLights}
          />,
          document.body
        )}
    </>
  );
}

// One status tile: a line icon with a glowing badge on its corner, and a
// two-line label under it.
function Tile({
  icon,
  wash,
  badge,
  badgeColor,
  label,
  status,
}: {
  icon: ReactNode;
  wash: TokenVar;
  badge: ReactNode;
  badgeColor: TokenVar;
  label: string;
  status: string;
}) {
  return (
    <StatusTile wash={wash} className="items-center gap-2.5 lg:gap-3 px-2.5 py-3.5 lg:px-4 lg:py-5">
      <span className="relative block w-7 h-7 lg:w-[34px] lg:h-[34px] text-fg">
        {icon}
        <span
          className="absolute -top-1.5 left-[calc(100%-7px)] lg:left-[calc(100%-8px)] min-w-[18px] h-[18px] lg:min-w-5 lg:h-5 px-1 rounded-full grid place-items-center dot-glow tabular text-[11px] lg:text-[13px] leading-none font-extrabold text-canvas"
          style={{
            backgroundColor: `var(${badgeColor})`,
            ['--dot-color' as string]: `var(${badgeColor})`,
          }}
        >
          {badge}
        </span>
      </span>
      <span className="text-[13px] lg:text-base font-semibold text-center leading-tight">
        {label}
        <br />
        {status}
      </span>
    </StatusTile>
  );
}

function StatusPart({
  icon,
  label,
  state,
  count,
  known,
}: {
  icon: ReactNode;
  label: string;
  state: 'open' | 'closed';
  count: number;
  known: boolean;
}) {
  const tone = !known ? TONE.idle : state === 'open' ? TONE.alert : TONE.secure;
  return (
    <Tile
      icon={icon}
      wash={tone.wash}
      badgeColor={tone.badge}
      badge={!known ? '–' : state === 'open' ? count : <CheckIcon size={10} weight="bold" />}
      label={label}
      status={state}
    />
  );
}

function LightsPart({ count, known }: { count: number; known: boolean }) {
  const lit = known && count > 0;
  return (
    <Tile
      icon={<BulbLineIcon />}
      wash={lit ? '--berg-yellow-soft' : '--berg-neutral-ghost'}
      badgeColor={lit ? '--berg-yellow' : '--berg-text-tertiary'}
      badge={known ? count : '–'}
      label={count === 1 ? 'Light' : 'Lights'}
      status="on"
    />
  );
}
